import { useCallback, useEffect, useState } from 'react';
import { gitMetrics, gitIdentities, employees as employeeApi } from '../api/client';
import { isoDate, daysAgo } from '../util/dates';
import { notify } from '../notify';

const VIEWS = [
  { key: 'leaderboard', label: 'Leaderboard' },
  { key: 'developer', label: 'Developer' },
  { key: 'identities', label: 'Identities' },
  { key: 'repos', label: 'Repositories' },
];


export default function GitMetrics() {
  const [view, setView] = useState('leaderboard');
  const [status, setStatus] = useState(null);
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(isoDate());
  const [busy, setBusy] = useState(null);
  const [progress, setProgress] = useState(null);
  const [scoring, setScoring] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await gitMetrics.status());
    } catch (e) {
      notify.error(e.message);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  /** One readable sentence per action - never a raw JSON dump. */
  function summarise(label, res) {
    if (label === 'Discover') {
      const list = Array.isArray(res) ? res : [];
      const tracked = list.filter((r) => r.tracked).length;
      return `Found ${list.length} repositor${list.length === 1 ? 'y' : 'ies'} · ${tracked} tracked.` +
        (tracked === 0 ? ' Choose which ones count under Repositories.' : '');
    }
    return typeof res === 'string' ? res : (res && res.message) || `${label} done`;
  }

  async function run(label, fn) {
    setBusy(label);
    try {
      const res = await fn();
      notify.success(summarise(label, res));
      await loadStatus();
      if (label === 'Discover') setView('repos');
    } catch (e) {
      notify.error(e.message);
    } finally {
      setBusy(null);
    }
  }

  /**
   * Collection runs in the background on the server - a full clone of dozens
   * of repos takes minutes - so start it, then poll until it finishes rather
   * than leaving the screen silent.
   */
  async function collect() {
    try {
      const res = await gitMetrics.collect();
      if (!res.started) {
        if (/no repositories are tracked/i.test(res.message || '')) {
          notify.warning('No repositories are tracked yet. Open Repositories and track the ones that count — or use “Track all”.');
          setView('repos');
        } else {
          notify.error(res.message);
        }
        return;
      }
      setBusy('Collect');
      pollProgress();
    } catch (e) {
      notify.error(e.message);
    }
  }

  function pollProgress() {
    const tick = async () => {
      try {
        const p = await gitMetrics.collectProgress();
        setProgress(p);
        if (p.running) {
          setTimeout(tick, 3000);
        } else {
          setBusy(null);
          setProgress(null);
          notify.success(
            `Collection finished: ${p.newCommits} new commit(s) from ${p.reposDone} repo(s)` +
            (p.reposFailed ? ` · ${p.reposFailed} failed` : '')
          );
          await loadStatus();
        }
      } catch (e) {
        setBusy(null);
        notify.error(e.message);
      }
    };
    tick();
  }

  /**
   * Scoring also runs in the background: every pending commit (and any that
   * failed before) in one go, several model calls at a time.
   */
  async function scoreAll() {
    const waiting = status ? (status.pending || 0) + (status.failed || 0) : null;
    if (waiting === 0) {
      notify.info('Nothing to score - every collected commit already has a score.');
      return;
    }
    if (waiting && !window.confirm(
      `Score ${waiting.toLocaleString('en-IN')} commit(s) now? Each one is a paid model call. ` +
      'You can stop the run at any point; finished scores are kept.')) return;
    try {
      const res = await gitMetrics.score();
      if (!res.started) {
        if (res.alreadyRunning) { setBusy('Score'); pollScoring(); return; }
        notify.warning(res.skipped || 'Scoring did not start');
        return;
      }
      setBusy('Score');
      pollScoring();
    } catch (e) {
      notify.error(e.message);
    }
  }

  async function stopScoring() {
    try {
      setScoring(await gitMetrics.stopScoring());
      notify.info('Stopping after the commits already being scored.');
    } catch (e) {
      notify.error(e.message);
    }
  }

  function pollScoring() {
    const tick = async () => {
      try {
        const p = await gitMetrics.scoreProgress();
        setScoring(p);
        if (p.running) {
          setTimeout(tick, 2000);
        } else {
          setBusy(null);
          setScoring(null);
          const msg = `Scoring finished: ${p.scored} scored` +
            (p.failed ? ` · ${p.failed} failed (will retry next run)` : '') +
            (p.skipped ? ` · ${p.skipped} skipped` : '');
          if (p.failed) notify.warning(msg); else notify.success(msg);
          await loadStatus();
          setRefreshKey((k) => k + 1);
        }
      } catch (e) {
        setBusy(null);
        notify.error(e.message);
      }
    };
    tick();
  }

  // Resume the progress display if a run is already going (e.g. after a reload).
  useEffect(() => {
    gitMetrics.collectProgress()
      .then((p) => { if (p && p.running) { setBusy('Collect'); pollProgress(); } })
      .catch(() => {});
    gitMetrics.scoreProgress()
      .then((p) => { if (p && p.running) { setBusy('Score'); pollScoring(); } })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div className="row-between">
        <div>
          <h2>Git metrics</h2>
          <p className="muted">
            Commit activity from tracked repositories, scored for substance.
          </p>
        </div>
        <div className="actions">
          <button className="btn" disabled={busy} onClick={() => run('Discover', gitMetrics.discover)}>
            {busy === 'Discover' ? 'Discovering…' : 'Discover repos'}
          </button>
          <button className="btn" disabled={busy} onClick={collect}>
            {busy === 'Collect' ? 'Collecting…' : 'Collect commits'}
          </button>
          <button className="btn btn-primary" disabled={busy} onClick={scoreAll}>
            {busy === 'Score' ? 'Scoring…' : 'Score all pending'}
          </button>
        </div>
      </div>

      {status && <StatusStrip status={status} />}

      {progress && progress.running && (
        <div className="alert alert-info">
          Collecting {progress.reposDone} / {progress.reposTotal} repos
          {' · '}{progress.newCommits} new commit(s)
          {progress.currentRepo && <> · now <span className="mono">{progress.currentRepo}</span></>}
          <div className="progress"><div className="progress-bar"
               style={{ width: `${progress.reposTotal ? (progress.reposDone / progress.reposTotal) * 100 : 0}%` }} /></div>
        </div>
      )}

      {scoring && scoring.running && (
        <div className="alert alert-info">
          <div className="row-between" style={{ marginBottom: 0, alignItems: 'center' }}>
            <span>
              {scoring.stopping ? 'Stopping… ' : 'Scoring '}
              <strong>{scoring.done.toLocaleString('en-IN')}</strong> / {scoring.total.toLocaleString('en-IN')} commits
              {scoring.failed > 0 && <> · {scoring.failed} failed</>}
              {' · '}{((scoring.promptTokens + scoring.completionTokens) / 1000).toFixed(0)}k tokens
              {scoring.model && <> · <span className="mono">{scoring.model}</span></>}
            </span>
            {!scoring.stopping && (
              <button className="btn btn-sm" onClick={stopScoring}>Stop</button>
            )}
          </div>
          <div className="progress"><div className="progress-bar"
               style={{ width: `${scoring.total ? (scoring.done / scoring.total) * 100 : 0}%` }} /></div>
        </div>
      )}

      <div className="subtabs">
        {VIEWS.map((v) => (
          <button
            key={v.key}
            className={`tab ${view === v.key ? 'tab-on' : ''}`}
            onClick={() => setView(v.key)}
          >
            {v.label}
          </button>
        ))}
        {(view === 'leaderboard' || view === 'developer') && (
          <div className="range">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <span className="muted">→</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        )}
      </div>

      {view === 'leaderboard' && <Leaderboard key={refreshKey} from={from} to={to} onError={notify.error} />}
      {view === 'developer' && <Developer key={refreshKey} from={from} to={to} onError={notify.error} />}
      {view === 'identities' && <Identities onError={notify.error} onNotice={notify.success} />}
      {view === 'repos' && <Repos onError={notify.error} onNotice={notify.success} onChanged={loadStatus} />}
    </div>
  );
}

function StatusStrip({ status }) {
  const cells = [
    ['Tracked repos', `${status.trackedRepos} / ${status.totalRepos}`],
    ['Commits', status.totalCommits],
    ['Scored', status.scored],
    ['Pending', status.pending],
    ...(status.failed ? [['Failed', status.failed]] : []),
    ['Filtered', status.filtered],
    ['Unmapped', status.unmappedCommits],
  ];
  return (
    <div className="strip">
      {cells.map(([label, value]) => (
        <div className="strip-cell" key={label}>
          <div className="strip-value">{value}</div>
          <div className="strip-label">{label}</div>
        </div>
      ))}
      {status.unmappedCommits > 0 && (
        <div className="strip-warn">
          {status.unmappedCommits} commit(s) belong to no employee — map them under
          <strong> Identities</strong>, or those people show as having done nothing.
        </div>
      )}
    </div>
  );
}

function Leaderboard({ from, to, onError }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    gitMetrics.leaderboard(from, to)
      .then((r) => live && setRows(r || []))
      .catch((e) => onError(e.message))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [from, to, onError]);

  if (loading) return <p className="muted">Loading…</p>;
  if (!rows.length) {
    return (
      <div className="card empty">
        <p><strong>No commit activity in this range.</strong></p>
        <p className="muted">Track a repository and run Collect, then Score.</p>
      </div>
    );
  }

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Developer</th><th>Dept</th><th>Score</th><th>Commits</th>
            <th>Meaningful</th><th>Active days</th><th>Churn</th><th>Unscored</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.employeeId}>
              <td>
                <strong>{r.name}</strong>
                <div className="small muted mono">{r.employeeCode}</div>
              </td>
              <td className="small">{r.department || '—'}</td>
              <td><strong>{r.weightedScore}</strong></td>
              <td>{r.commits}</td>
              <td>
                {r.scoredCommits > 0
                  ? `${r.meaningfulCommits}/${r.scoredCommits}`
                  : <span className="muted">—</span>}
              </td>
              <td>{r.activeDays}</td>
              <td className="small muted">{r.churn}</td>
              <td className="small">
                {r.pendingCommits > 0
                  ? <span className="pill pill-probation">{r.pendingCommits} pending</span>
                  : <span className="muted">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="small muted note">
        Score weights complexity by work type; commits judged not meaningful contribute
        zero. Volume alone cannot raise it.
      </p>
    </div>
  );
}

function Developer({ from, to, onError }) {
  const [staff, setStaff] = useState([]);
  const [selected, setSelected] = useState('');
  const [detail, setDetail] = useState(null);
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    employeeApi.list().then(setStaff).catch((e) => onError(e.message));
  }, [onError]);

  useEffect(() => {
    if (!selected) { setDetail(null); setDays([]); return; }
    setLoading(true);
    Promise.all([
      gitMetrics.developer(selected, from, to),
      gitMetrics.timeline(selected, from, to),
    ])
      .then(([d, t]) => { setDetail(d); setDays(t || []); })
      .catch((e) => onError(e.message))
      .finally(() => setLoading(false));
  }, [selected, from, to, onError]);

  const max = days.reduce((m, d) => Math.max(m, d.commits), 0) || 1;

  return (
    <div>
      <label className="field" style={{ maxWidth: 320, marginBottom: 16 }}>
        <span>Developer</span>
        <select value={selected} onChange={(e) => setSelected(e.target.value)}>
          <option value="">Select…</option>
          {staff.map((s) => (
            <option key={s.id} value={s.id}>{s.employeeCode} · {s.fullName}</option>
          ))}
        </select>
      </label>

      {loading && <p className="muted">Loading…</p>}

      {detail && !loading && (
        <>
          <div className="strip">
            <div className="strip-cell"><div className="strip-value">{detail.weightedScore}</div><div className="strip-label">Score</div></div>
            <div className="strip-cell"><div className="strip-value">{detail.totalCommits}</div><div className="strip-label">Commits</div></div>
            <div className="strip-cell"><div className="strip-value">{detail.meaningfulCommits}</div><div className="strip-label">Meaningful</div></div>
            <div className="strip-cell"><div className="strip-value">{detail.filteredCommits}</div><div className="strip-label">Filtered out</div></div>
            <div className="strip-cell"><div className="strip-value">{detail.pendingCommits}</div><div className="strip-label">Not scored</div></div>
          </div>

          {days.length > 0 && (
            <div className="card">
              <h3>Activity</h3>
              <div className="spark">
                {days.map((d) => (
                  <div className="spark-col" key={d.date} title={`${d.date}: ${d.commits} commit(s)`}>
                    <div className="spark-bar" style={{ height: `${(d.commits / max) * 100}%` }} />
                  </div>
                ))}
              </div>
              <p className="small muted">{days.length} active day(s) between {detail.from} and {detail.to}</p>
            </div>
          )}

          {detail.commits.length === 0 ? (
            <div className="card empty"><p className="muted">No commits in this range.</p></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Date</th><th>Repo</th><th>Commit</th><th>Lines</th><th>Assessment</th></tr>
                </thead>
                <tbody>
                  {detail.commits.map((c) => (
                    <tr key={c.sha + c.repo}>
                      <td className="small nowrap">{c.date}</td>
                      <td className="small">{c.repo}</td>
                      <td>
                        <div>{c.message}</div>
                        <div className="small muted mono">
                          {c.sha}{c.branches ? ` · ${c.branches}` : ''}
                        </div>
                      </td>
                      <td className="small nowrap">
                        <span className="add">+{c.insertions}</span>{' '}
                        <span className="del">−{c.deletions}</span>
                      </td>
                      <td>
                        {c.state === 'FILTERED' && (
                          <span className="small muted">excluded — {c.filterReason}</span>
                        )}
                        {c.state === 'PENDING' && <span className="small muted">not scored yet</span>}
                        {c.state === 'FAILED' && <span className="small">scoring failed</span>}
                        {c.state === 'SCORED' && (
                          <div>
                            <span className={`pill ${c.meaningful ? 'pill-active' : 'pill-exited'}`}>
                              {c.impact} · {c.complexity}/10
                            </span>
                            <div className="small reason">{c.reasoning}</div>
                            {c.risk && <div className="small risk">⚠ {c.risk}</div>}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Identities({ onError, onNotice }) {
  const [unmapped, setUnmapped] = useState([]);
  const [mapped, setMapped] = useState([]);
  const [staff, setStaff] = useState([]);
  const [choice, setChoice] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, m, s] = await Promise.all([
        gitIdentities.unmapped(), gitIdentities.list(), employeeApi.list(),
      ]);
      setUnmapped(u || []); setMapped(m || []); setStaff(s || []);
    } catch (e) {
      onError(e.message);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => { load(); }, [load]);

  /**
   * Single source of truth for "who is selected for this row".
   *
   * The dropdown falls back to the server's suggestion when the user has not
   * touched it, so map() has to honour the same fallback - reading only
   * `choice` meant an untouched row looked selected, enabled the button, and
   * then did nothing at all when clicked.
   */
  function selectedFor(u) {
    const v = choice[u.authorEmail] ?? u.suggestedEmployeeId ?? '';
    return v === '' || v === null || v === undefined ? null : Number(v);
  }

  async function map(u) {
    const employeeId = selectedFor(u);
    if (!employeeId) {
      onError(`Pick an employee for ${u.authorEmail} first.`);
      return;
    }
    try {
      await gitIdentities.create(employeeId, u.authorEmail);
      onNotice(`Mapped ${u.authorEmail}`);
      await load();
    } catch (e) {
      onError(e.message);
    }
  }

  if (loading) return <p className="muted">Loading…</p>;

  return (
    <div>
      <div className="card">
        <h3>Unmapped commit authors</h3>
        <p className="muted small">
          Commits from these addresses count for nobody. One person often has several —
          work email, personal laptop, GitHub's privacy noreply address.
        </p>
        {unmapped.length === 0 ? (
          <p className="muted">Nothing unmapped.</p>
        ) : (
          <table>
            <thead><tr><th>Author email</th><th>Git name(s)</th><th>Commits</th><th>Map to employee</th><th></th></tr></thead>
            <tbody>
              {unmapped.map((u) => (
                <tr key={u.authorEmail}>
                  <td className="mono small">
                    {u.authorEmail}
                    {u.githubLogin && <div className="small muted">GitHub: {u.githubLogin}</div>}
                  </td>
                  <td><AuthorNames names={u.authorNames} /></td>
                  <td className="small nowrap">
                    <strong>{u.commits}</strong>
                    {u.lastCommit && <div className="muted">last {u.lastCommit}</div>}
                  </td>
                  <td>
                    <select
                      value={selectedFor(u) ?? ''}
                      onChange={(e) => setChoice({ ...choice, [u.authorEmail]: e.target.value })}
                    >
                      <option value="">—</option>
                      {staff.map((s) => (
                        <option key={s.id} value={s.id}>{s.employeeCode} · {s.fullName}</option>
                      ))}
                    </select>
                    {u.suggestedName && (
                      <div className="small muted">
                        suggested: {u.suggestedName}
                        {u.suggestedBy && <> · by {u.suggestedBy}</>}
                      </div>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => map(u)}
                      disabled={!selectedFor(u)}
                    >
                      Map
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr><th>Identity</th><th>Git name(s)</th><th>Commits</th><th>Employee</th><th></th></tr></thead>
          <tbody>
            {mapped.length === 0 && (
              <tr><td colSpan={5} className="muted">No identities mapped yet.</td></tr>
            )}
            {mapped.map((m) => {
              const e = staff.find((s) => s.id === m.employeeId);
              return (
                <tr key={m.id}>
                  <td className="mono small">{m.value}</td>
                  <td><AuthorNames names={m.authorNames} /></td>
                  <td className="small">{m.commits || 0}</td>
                  <td>{e ? `${e.employeeCode} · ${e.fullName}` : `#${m.employeeId}`}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={async () => {
                        try { await gitIdentities.del(m.id); onNotice('Removed'); await load(); }
                        catch (err) { onError(err.message); }
                      }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Repos({ onError, onNotice, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await gitMetrics.repos()) || []); }
    catch (e) { onError(e.message); }
    finally { setLoading(false); }
  }, [onError]);

  useEffect(() => { load(); }, [load]);

  async function toggle(repo) {
    try {
      await gitMetrics.setTracked(repo.id, !repo.tracked);
      onNotice(`${repo.fullName} ${repo.tracked ? 'untracked' : 'tracked'}`);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      onError(e.message);
    }
  }

  async function trackAll() {
    try {
      const res = await gitMetrics.trackAll();
      onNotice(`Tracking all ${res.tracked} repositories. Run “Collect commits” next.`);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      onError(e.message);
    }
  }

  if (loading) return <p className="muted">Loading…</p>;
  if (!rows.length) {
    return (
      <div className="card empty">
        <p><strong>No repositories discovered.</strong></p>
        <p className="muted">Configure a GitHub PAT, then press Discover repos.</p>
      </div>
    );
  }

  const trackedCount = rows.filter((r) => r.tracked).length;

  return (
    <div>
    <div className="row-between" style={{ marginBottom: 10 }}>
      <p className="muted" style={{ margin: 0 }}>
        <strong>{trackedCount}</strong> of {rows.length} tracked · only tracked repositories are collected
      </p>
      {trackedCount < rows.length && (
        <button className="btn btn-primary btn-sm" onClick={trackAll}>
          Track all {rows.length}
        </button>
      )}
    </div>
    <div className="table-wrap">
      <table>
        <thead>
          <tr><th>Repository</th><th>Visibility</th><th>Last sync</th><th>Tracked</th><th></th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className={r.tracked ? '' : 'exited'}>
              <td>
                <div className="mono small">{r.fullName}</div>
                {r.lastSyncError && <div className="small risk">⚠ {r.lastSyncError}</div>}
              </td>
              <td className="small">{r.privateRepo ? 'private' : 'public'}</td>
              <td className="small muted">
                {r.lastSyncedAt ? String(r.lastSyncedAt).replace('T', ' ').slice(0, 16) : '—'}
                {r.lastSyncCommitCount != null && ` · ${r.lastSyncCommitCount} new`}
              </td>
              <td>
                <span className={`pill ${r.tracked ? 'pill-active' : 'pill-exited'}`}>
                  {r.tracked ? 'YES' : 'NO'}
                </span>
              </td>
              <td>
                <button className="btn btn-sm" onClick={() => toggle(r)}>
                  {r.tracked ? 'Untrack' : 'Track'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="small muted note">
        Tracking is opt-in: a PAT also sees personal repositories, which should not be
        scored as work.
      </p>
    </div>
    </div>
  );
}

/**
 * Git author names seen under one email, busiest first. When an email carries
 * more than one name it is flagged - often a shared machine or a git config
 * copied from someone else, and exactly where a mapping goes wrong.
 */
function AuthorNames({ names }) {
  const list = (names || []).filter((n) => n && n.name);
  if (!list.length) return <span className="muted small">—</span>;
  return (
    <div className="author-names">
      {list.map((n) => (
        <div key={n.name} className="author-name">
          <span>{n.name}</span>
          <span className="muted small"> · {n.commits}</span>
        </div>
      ))}
      {list.length > 1 && <div className="small warn-text">{list.length} different names on one email</div>}
    </div>
  );
}
